import type { ErrorComponentProps } from '@tanstack/react-router';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { reportError } from '@/lib/error-reporter';
import { isStaleBuildError, reloadForNewVersion } from '@/lib/stale-build';

/**
 * Shown when a route crashes or its loader fails. Reports the crash (unless the
 * server already logged it) and shows the id to quote.
 */
export function ErrorFallback({ error, info, reset }: ErrorComponentProps) {
  const stale = isStaleBuildError(error);
  useEffect(() => {
    // A new version was deployed while this tab was open: load it instead of failing.
    if (stale && reloadForNewVersion()) return;
    reportError('react_boundary', error, { componentStack: info?.componentStack });
  }, [error, info, stale]);

  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const title =
    error instanceof ApiError && error.status === 403
      ? 'You do not have access to this page'
      : 'Something went wrong';

  return (
    <div role="alert" className="mx-auto mt-24 max-w-md px-4 text-center">
      <AlertTriangle className="mx-auto size-10 text-danger" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {error instanceof ApiError
          ? error.message
          : 'The page failed to load. It has been reported.'}
      </p>
      {requestId && (
        <p className="mt-3 font-mono text-xs text-muted-foreground">
          Request id: <span className="select-all">{requestId}</span>
        </p>
      )}
      <Button
        className="mt-6"
        variant="outline"
        onClick={() => {
          reset();
          window.location.reload();
        }}
      >
        <RotateCw /> Reload
      </Button>
    </div>
  );
}
